<template>
  <el-dialog
    title="录音和位置"
    :visible.sync="visible"
    width="720px"
    top="6vh"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <div v-loading="loading" class="device-data-dialog">
      <template v-if="detail">
        <section class="device-section">
          <div class="section-title">播放录音</div>
          <template v-if="audio.available">
            <div class="audio-meta">
              <span>{{ audio.original_name || '讲解录音' }}</span>
              <span>{{ formatDuration(audio.duration) }}</span>
              <span>{{ formatFileSize(audio.file_size) }}</span>
            </div>
            <audio ref="audio" :src="audio.play_url" controls preload="metadata"></audio>
          </template>
          <div v-else class="empty-text">暂无录音</div>
        </section>

        <section class="device-section">
          <div class="section-title section-title-actions">
            <span>音频文本</span>
            <el-button
              v-if="recording && recording.audio_text"
              type="text"
              size="mini"
              icon="el-icon-download"
              @click="downloadAudioText"
            >
              下载文本
            </el-button>
          </div>
          <div v-if="recording && recording.audio_text" class="audio-text">
            {{ recording.audio_text }}
          </div>
          <div v-else class="empty-text">暂无音频文本</div>
        </section>

        <section class="device-section">
          <div class="section-title">
            位置信息
            <span v-if="trajectory.total_count" class="position-count">
              共 {{ trajectory.total_count }} 个位置点<span v-if="trajectory.sampled"
                >，已抽稀展示</span
              >
            </span>
          </div>
          <div v-if="trajectory.points.length" class="map-wrapper">
            <div ref="map" class="map-container"></div>
            <div v-if="mapLoading" class="map-mask">地图加载中...</div>
            <div v-if="mapError" class="map-error">
              {{ mapError }}
              <div class="coordinate-summary">{{ coordinateSummary }}</div>
            </div>
          </div>
          <div v-else class="empty-text">暂无位置信息</div>
        </section>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import { saveAs } from 'file-saver'
import { getDeviceData } from '@/api/assignRecord'
import { cancelCoordinateConversions, convertGpsPoints, loadTMap } from '@/utils/tmap'

export default {
  name: 'DeviceDataDialog',
  data() {
    return {
      visible: false,
      loading: false,
      mapLoading: false,
      mapError: '',
      detail: null,
      map: null,
      mapRenderId: 0
    }
  },
  computed: {
    recording() {
      return (this.detail && this.detail.recording) || null
    },
    audio() {
      return (this.recording && this.recording.audio) || { available: false }
    },
    trajectory() {
      return (
        (this.detail && this.detail.trajectory) || {
          total_count: 0,
          sampled: false,
          points: []
        }
      )
    },
    coordinateSummary() {
      const points = this.trajectory.points
      if (!points.length) return ''
      const first = points[0]
      const last = points[points.length - 1]
      return `起点：${first.longitude}, ${first.latitude}；终点：${last.longitude}, ${last.latitude}`
    }
  },
  methods: {
    open(guideServiceId) {
      this.visible = true
      this.loading = true
      this.detail = null
      this.mapError = ''
      getDeviceData(guideServiceId)
        .then((response) => {
          this.detail = response.data
          if (this.trajectory.points.length) {
            this.$nextTick(() => this.renderMap())
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    async renderMap() {
      this.destroyMap()
      const renderId = this.mapRenderId
      this.mapLoading = true
      this.mapError = ''
      try {
        const TMap = await loadTMap()
        const converted = await convertGpsPoints(this.trajectory.points)
        if (renderId !== this.mapRenderId || !this.visible || !this.$refs.map) return

        const path = converted.map((point) => new TMap.LatLng(point.lat, point.lng))
        this.map = new TMap.Map(this.$refs.map, {
          center: path[0],
          zoom: 15,
          viewMode: '2D'
        })

        new TMap.MultiPolyline({
          map: this.map,
          styles: {
            trajectory: new TMap.PolylineStyle({
              color: '#409EFF',
              width: 5,
              borderWidth: 0,
              lineCap: 'round'
            })
          },
          geometries: [
            {
              id: 'trajectory',
              styleId: 'trajectory',
              paths: path
            }
          ]
        })

        const markerIndexes = this.markerIndexes(path.length)
        const markerGeometries = markerIndexes.map((index) => ({
          id: String(index),
          styleId: 'position',
          position: path[index]
        }))
        const labelGeometries = markerIndexes.map((index) => ({
          id: String(index),
          styleId: 'sequence',
          position: path[index],
          content: String(this.trajectory.points[index].seq)
        }))
        const marker = new TMap.MultiMarker({
          map: this.map,
          zIndex: 1,
          styles: {
            position: new TMap.MarkerStyle({
              width: 24,
              height: 24,
              anchor: { x: 12, y: 12 },
              src: 'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2224%22 height=%2224%22%3E%3Ccircle cx=%2212%22 cy=%2212%22 r=%2211%22 fill=%22%23303133%22 stroke=%22white%22 stroke-width=%222%22/%3E%3C/svg%3E'
            })
          },
          geometries: markerGeometries
        })
        const label = new TMap.MultiLabel({
          map: this.map,
          zIndex: 2,
          styles: {
            sequence: new TMap.LabelStyle({
              color: '#ffffff',
              size: 12,
              width: 24,
              height: 24,
              alignment: 'center',
              verticalAlignment: 'middle',
              offset: { x: 0, y: 0 }
            })
          },
          geometries: labelGeometries
        })
        const infoWindow = new TMap.InfoWindow({
          map: this.map,
          position: path[0],
          offset: { x: 0, y: -18 }
        })
        infoWindow.close()
        const openInfoWindow = (event) => {
          const index = Number(event.geometry.id)
          const source = this.trajectory.points[index]
          infoWindow.setContent(
            `<div style="line-height:22px">第 ${this.escapeHtml(
              source.seq
            )} 个位置点<br>${this.escapeHtml(source.upload_time)}<br>${this.escapeHtml(
              source.longitude
            )}, ${this.escapeHtml(source.latitude)}</div>`
          )
          infoWindow.setPosition(path[index])
          infoWindow.open()
        }
        marker.on('click', openInfoWindow)
        label.on('click', openInfoWindow)

        if (path.length === 1) {
          this.map.setCenter(path[0])
          this.map.setZoom(16)
        } else {
          const bounds = new TMap.LatLngBounds(path[0], path[0])
          path.slice(1).forEach((position) => bounds.extend(position))
          this.map.fitBounds(bounds, { padding: 40 })
        }
      } catch (error) {
        if (renderId === this.mapRenderId) {
          this.mapError = error.message || '地图加载失败'
        }
      } finally {
        if (renderId === this.mapRenderId) this.mapLoading = false
      }
    },
    markerIndexes(length) {
      if (length <= 100) return Array.from({ length }, (_, index) => index)
      const indexes = new Set()
      for (let index = 0; index < 100; index += 1) {
        indexes.add(Math.round((index * (length - 1)) / 99))
      }
      return Array.from(indexes).sort((a, b) => a - b)
    },
    downloadAudioText() {
      if (!this.recording || !this.recording.audio_text) return

      const originalName = this.audio.original_name || ''
      const fallbackName = `guide-service-${
        (this.detail && this.detail.guide_service_id) || 'recording'
      }`
      const baseName = (originalName.replace(/\.[^.]+$/, '') || fallbackName).replace(
        /[\\/:*?"<>|]/g,
        '_'
      )
      const blob = new Blob([`\ufeff${this.recording.audio_text}`], {
        type: 'text/plain;charset=utf-8'
      })
      saveAs(blob, `${baseName}-音频文本.txt`)
    },
    escapeHtml(value) {
      return String(value == null ? '' : value).replace(/[&<>"']/g, (character) => {
        const entities = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        }
        return entities[character]
      })
    },
    formatDuration(seconds) {
      const value = Number(seconds || 0)
      const hours = Math.floor(value / 3600)
      const minutes = Math.floor((value % 3600) / 60)
      const rest = value % 60
      return [hours, minutes, rest].map((item) => String(item).padStart(2, '0')).join(':')
    },
    formatFileSize(bytes) {
      const value = Number(bytes || 0)
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
      return `${(value / 1024 / 1024).toFixed(1)} MB`
    },
    destroyMap() {
      this.mapRenderId += 1
      cancelCoordinateConversions()
      if (this.map) {
        this.map.destroy()
        this.map = null
      }
    },
    handleClosed() {
      if (this.$refs.audio) {
        this.$refs.audio.pause()
        this.$refs.audio.removeAttribute('src')
        this.$refs.audio.load()
      }
      this.destroyMap()
      this.detail = null
      this.mapError = ''
    }
  }
}
</script>

<style scoped>
.device-data-dialog {
  min-height: 180px;
  max-height: 76vh;
  overflow-y: auto;
  padding-right: 4px;
}
.device-section + .device-section {
  margin-top: 26px;
}
.section-title {
  margin-bottom: 12px;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}
.section-title-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.audio-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-bottom: 10px;
  color: #909399;
  font-size: 13px;
}
audio {
  width: 100%;
  height: 42px;
}
.audio-text {
  box-sizing: border-box;
  max-height: 240px;
  padding: 12px;
  overflow-y: auto;
  color: #606266;
  background: #f8f9fb;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
}
.empty-text {
  padding: 18px 0;
  color: #909399;
  text-align: center;
  background: #f8f9fb;
}
.position-count {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
  font-weight: normal;
}
.map-wrapper {
  position: relative;
  min-height: 340px;
}
.map-container {
  width: 100%;
  height: 340px;
}
.map-mask,
.map-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  background: rgba(248, 249, 251, 0.92);
}
.map-error {
  flex-direction: column;
  color: #f56c6c;
}
.coordinate-summary {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}
@media (max-width: 760px) {
  .map-container,
  .map-wrapper {
    height: 280px;
    min-height: 280px;
  }
}
</style>
